import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { Plus, X, ArrowUp, ArrowDown } from "lucide-react";
import { useEditMode } from "@/components/editor/EditModeContext";
import EditableText from "@/components/editor/EditableText";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_FAQS = [
  { q: "How does the exclusive booking work?", a: "When you book Whale Cove, you get the entire property — all 3 RV/trailer sites. No other groups, no shared spaces." },
  { q: "What size RVs or trailers can the site accommodate?", a: "Standard RVs and travel trailers do well here. Reach out about larger rigs." },
  { q: "Is there power and water on site?", a: "There are no power or water hookups on site. Bring a generator for electricity and arrive with full water tanks. All RVs and campers must be fully self-contained." },
  { q: "What about waste/pump-out?", a: "Pump-out facility is a short drive away." },
  { q: "Is there cell service?", a: "Limited to none. We consider this a feature." },
  { q: "When is the best time to visit?", a: "June through October." },
  { q: "Is there a minimum stay?", a: "Minimum 2 nights. We recommend at least 3." },
  { q: "Is Whale Cove pet-friendly?", a: "Yes — dogs are welcome, on-leash or off. You know your dog best, and we trust you to make the call. A few heads-ups: the shoreline is rocky and the tides move fast (20+ feet, twice a day), so keep an eye on them near the water. There's working land on the Neck — the occasional fishing gear, the odd piece of equipment, and neighbours with their own dogs and routines. Pack out waste, and please don't let them chase wildlife. Beyond that, let them run." },
];

function EditableFaqItem({ faq, index, editMode, onUpdate, onDelete, onMove, total }) {
  const qRef = useRef(null);
  const aRef = useRef(null);

  if (!editMode) {
    return (
      <AccordionItem
        value={`faq-${faq.id || index}`}
        className="border-b border-wc-driftwood/30 border-l-0 border-r-0 border-t-0"
      >
        <AccordionTrigger
          className="font-display text-wc-forest text-base sm:text-lg font-medium py-5 hover:no-underline"
          data-testid={`faq-trigger-${index}`}
        >
          {faq.q}
        </AccordionTrigger>
        <AccordionContent className="font-body text-wc-slate text-sm leading-relaxed">
          {faq.a}
        </AccordionContent>
      </AccordionItem>
    );
  }

  return (
    <div className="group relative border-b border-wc-driftwood/30 py-4">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div
            ref={qRef}
            className="font-display text-wc-forest text-base sm:text-lg font-medium mb-2 outline-none cursor-text hover:outline-dashed hover:outline-1 hover:outline-offset-2 hover:outline-[#7A9E6E80] focus:outline-dashed focus:outline-2 focus:outline-offset-2 focus:outline-[#7A9E6E]"
            contentEditable
            suppressContentEditableWarning
            onBlur={() => {
              const t = qRef.current?.innerText?.trim();
              if (t && t !== faq.q) onUpdate(faq.id, { q: t });
            }}
          >
            {faq.q}
          </div>
          <div
            ref={aRef}
            className="font-body text-wc-slate text-sm leading-relaxed outline-none cursor-text hover:outline-dashed hover:outline-1 hover:outline-offset-2 hover:outline-[#7A9E6E80] focus:outline-dashed focus:outline-2 focus:outline-offset-2 focus:outline-[#7A9E6E]"
            contentEditable
            suppressContentEditableWarning
            onBlur={() => {
              const t = aRef.current?.innerText?.trim();
              if (t && t !== faq.a) onUpdate(faq.id, { a: t });
            }}
          >
            {faq.a}
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity pt-1">
          {index > 0 && (
            <button onClick={() => onMove(index, index - 1)}
              className="w-6 h-6 rounded-full bg-wc-forest text-white flex items-center justify-center shadow">
              <ArrowUp className="w-3 h-3" />
            </button>
          )}
          {index < total - 1 && (
            <button onClick={() => onMove(index, index + 1)}
              className="w-6 h-6 rounded-full bg-wc-forest text-white flex items-center justify-center shadow">
              <ArrowDown className="w-3 h-3" />
            </button>
          )}
          <button onClick={() => onDelete(faq.id)}
            className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow">
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const { editMode, authHeader } = useEditMode();
  const [faqs, setFaqs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const fetchFaqs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/faqs/public`);
      setFaqs(res.data.faqs.length > 0 ? res.data.faqs : FALLBACK_FAQS);
    } catch {
      setFaqs(FALLBACK_FAQS);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  const updateFaq = async (id, updates) => {
    setFaqs(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
    try { await axios.put(`${API}/admin/faqs/${id}`, updates, authHeader); } catch (e) { console.error(e); }
  };

  const deleteFaq = async (id) => {
    setFaqs(prev => prev.filter(f => f.id !== id));
    try { await axios.delete(`${API}/admin/faqs/${id}`, authHeader); } catch (e) { console.error(e); }
  };

  const addFaq = async () => {
    try {
      const res = await axios.post(`${API}/admin/faqs`, {
        q: "New Question?", a: "Click to edit the answer.",
      }, authHeader);
      setFaqs(prev => [...prev, res.data.faq]);
    } catch (e) { console.error(e); }
  };

  const moveFaq = async (fromIdx, toIdx) => {
    const next = [...faqs];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setFaqs(next);
    const order = next.map(f => f.id).filter(Boolean);
    try { await axios.put(`${API}/admin/features/reorder`, { order }, authHeader); } catch (e) { console.error(e); }
  };

  if (!loaded) return null;

  return (
    <section id="faq" data-testid="faq-section" className="wc-section bg-white" ref={ref}>
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p className="font-accent italic text-wc-teal text-base mb-3">FAQ</p>
          <EditableText contentKey="faq_heading" as="h2"
            className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4"
            defaultValue="Common questions." />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {editMode ? (
            <div data-testid="faq-edit-mode">
              {faqs.map((faq, i) => (
                <EditableFaqItem
                  key={faq.id || i} faq={faq} index={i}
                  editMode={editMode} onUpdate={updateFaq}
                  onDelete={deleteFaq} onMove={moveFaq} total={faqs.length}
                />
              ))}
              <button
                onClick={addFaq}
                className="mt-4 w-full py-3 border-2 border-dashed border-wc-brass/40 rounded-md text-wc-brass font-body text-sm flex items-center justify-center gap-2 hover:border-wc-brass hover:bg-wc-brass/5 transition-all"
                data-testid="add-faq-button"
              >
                <Plus className="w-4 h-4" /> Add Question
              </button>
            </div>
          ) : (
            <Accordion type="single" collapsible data-testid="faq-accordion">
              {faqs.map((faq, i) => (
                <EditableFaqItem
                  key={faq.id || i} faq={faq} index={i}
                  editMode={false} onUpdate={updateFaq}
                  onDelete={deleteFaq} onMove={moveFaq} total={faqs.length}
                />
              ))}
            </Accordion>
          )}
        </motion.div>
      </div>
    </section>
  );
}
